import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';

interface SessionMessage {
  id: string
  session_id: string
  user_id: string
  message: string
  created_at: string
  profiles: {
    nickname: string
    avatar_url: string | null
  }
}

interface Court {
  id: string
  name: string
  location: string
  district: string
  address?: string
}

interface CreateSessionForm {
  title: string
  description: string
  courtId: string
  date: string
  startTime: string
  endTime: string
  maxParticipants: number
  skillLevel: string
  price: string
  equipment: string
  notes: string
}

const Sessions: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'upcoming' | 'my-bookings'>('upcoming');
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showChatModal, setShowChatModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedSession, setSelectedSession] = useState<any>(null);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [chatMessages, setChatMessages] = useState<SessionMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [availableCourts, setAvailableCourts] = useState<Court[]>([]);
  const [courtsLoading, setCourtsLoading] = useState(false);
  const [createForm, setCreateForm] = useState<CreateSessionForm>({
    title: '',
    description: '',
    courtId: '',
    date: '',
    startTime: '',
    endTime: '',
    maxParticipants: 4,
    skillLevel: 'Mixed',
    price: '',
    equipment: '',
    notes: ''
  });
  const chatEndRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const { t } = useTranslation('common');

  // Load courts from database
  const loadCourts = async () => {
    setCourtsLoading(true);
    try {
      const { data, error } = await supabase
        .from('courts')
        .select('id, name, location, district, address')
        .order('name');

      if (error) throw error;
      setAvailableCourts(data || []);
    } catch (error) {
      console.error('Error loading courts:', error);
      // Fallback to some basic courts if database fails
      setAvailableCourts([
        { id: '1', name: 'Central Park Courts', location: 'Central', district: 'Central', address: '123 Central Park Road, Central, Hong Kong' },
        { id: '2', name: 'Victoria Harbour Sports Complex', location: 'Tsim Sha Tsui', district: 'Yau Tsim Mong', address: '456 Victoria Harbour Road, Tsim Sha Tsui, Hong Kong' },
        { id: '3', name: 'Aberdeen Community Courts', location: 'Aberdeen', district: 'Southern', address: '789 Aberdeen Main Road, Aberdeen, Hong Kong' }
      ]);
    } finally {
      setCourtsLoading(false);
    }
  };

  // Load courts when component mounts
  useEffect(() => {
    loadCourts();
  }, []);

  // Helper function to get skill level colors
  const getSkillLevelColor = (skillLevel: string) => {
    switch (skillLevel.toLowerCase()) {
      case 'beginner':
        return 'bg-green-100 text-green-800';
      case 'intermediate':
        return 'bg-blue-100 text-blue-800';
      case 'advanced':
        return 'bg-purple-100 text-purple-800';
      case 'expert':
        return 'bg-red-100 text-red-800';
      case 'mixed':
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Convert sessions to state
  const [sessions, setSessions] = useState([
    {
      id: 'a1b2c3d4-e5f6-4890-abcd-ef1234567890',
      title: 'Morning Doubles Practice',
      date: '2024-01-20',
      time: '08:00 - 10:00',
      court: 'Central Park Courts',
      courtId: 1,
      creator: 'Sarah Chen',
      creatorId: 2,
      maxPlayers: 8,
      currentPlayers: 6,
      skillLevel: 'Intermediate',
      description: 'Join us for a fun doubles practice session. All skill levels welcome!',
      price: 'HK$60 per person',
      participants: [
        { id: 2, name: 'Sarah Chen', avatar: 'SC', skillLevel: 'Advanced', joinedDate: '2023-06-15' },
        { id: 3, name: 'Mike Wong', avatar: 'MW', skillLevel: 'Intermediate', joinedDate: '2023-08-20' },
        { id: 4, name: 'Lisa Park', avatar: 'LP', skillLevel: 'Intermediate', joinedDate: '2023-07-10' },
        { id: 5, name: 'David Kim', avatar: 'DK', skillLevel: 'Beginner', joinedDate: '2023-09-05' },
        { id: 6, name: 'Emma Liu', avatar: 'EL', skillLevel: 'Intermediate', joinedDate: '2023-08-30' },
        { id: 7, name: 'Tom Zhang', avatar: 'TZ', skillLevel: 'Advanced', joinedDate: '2023-05-22' }
      ],
      isBooked: false,
      status: 'open',
      equipment: 'Paddles and balls provided',
      notes: 'Please bring water and wear comfortable athletic shoes. Court has shade and parking available.',
      courtDetails: {
        address: '123 Central Park Road, Central, Hong Kong',
        facilities: ['Parking Available', 'Restrooms', 'Water Fountain', 'Shade Area'],
        surface: 'Outdoor Hard Court',
        lighting: 'Natural Light'
      }
    },
    {
      id: 'b2c3d4e5-f6a7-4901-bcde-f23456789012',
      title: 'Beginner Friendly Session',
      date: '2024-01-20',
      time: '14:00 - 16:00',
      court: 'Victoria Harbour Sports Complex',
      courtId: 2,
      creator: 'Mike Wong',
      creatorId: 3,
      maxPlayers: 6,
      currentPlayers: 4,
      skillLevel: 'Beginner',
      description: "Perfect for newcomers to pickleball. We'll cover basics and have fun games.",
      price: 'HK$40 per person',
      participants: [
        { id: 3, name: 'Mike Wong', avatar: 'MW', skillLevel: 'Intermediate', joinedDate: '2023-08-20' },
        { id: 8, name: 'Anna Chen', avatar: 'AC', skillLevel: 'Beginner', joinedDate: '2023-11-15' },
        { id: 9, name: 'Peter Lee', avatar: 'PL', skillLevel: 'Beginner', joinedDate: '2023-12-01' },
        { id: 10, name: 'Jenny Wu', avatar: 'JW', skillLevel: 'Beginner', joinedDate: '2023-11-28' }
      ],
      isBooked: true,
      status: 'open',
      equipment: 'Beginner paddles available for rent (HK$10)',
      notes: "Great for first-time players! We'll start with basic rules and techniques.",
      courtDetails: {
        address: '456 Victoria Harbour Road, Tsim Sha Tsui, Hong Kong',
        facilities: ['Indoor Courts', 'Air Conditioning', 'Parking', 'Pro Shop'],
        surface: 'Indoor Synthetic Court',
        lighting: 'LED Lighting'
      }
    },
    {
      id: 'c3d4e5f6-a7b8-4012-cdef-345678901234',
      title: 'Competitive Tournament Prep',
      date: '2024-01-21',
      time: '10:00 - 12:00',
      court: 'Aberdeen Community Courts',
      courtId: 3,
      creator: 'Lisa Park',
      creatorId: 4,
      maxPlayers: 8,
      currentPlayers: 8,
      skillLevel: 'Advanced',
      description: 'High-intensity training for upcoming tournaments. Advanced players only.',
      price: 'HK$80 per person',
      participants: [
        { id: 4, name: 'Lisa Park', avatar: 'LP', skillLevel: 'Advanced', joinedDate: '2023-07-10' },
        { id: 11, name: 'Kevin Tan', avatar: 'KT', skillLevel: 'Advanced', joinedDate: '2023-04-18' },
        { id: 12, name: 'Rachel Ho', avatar: 'RH', skillLevel: 'Advanced', joinedDate: '2023-06-03' },
        { id: 13, name: 'Steven Lau', avatar: 'SL', skillLevel: 'Advanced', joinedDate: '2023-05-12' },
        { id: 14, name: 'Grace Ng', avatar: 'GN', skillLevel: 'Advanced', joinedDate: '2023-07-25' },
        { id: 15, name: 'Alex Wong', avatar: 'AW', skillLevel: 'Advanced', joinedDate: '2023-03-30' },
        { id: 16, name: 'Cathy Li', avatar: 'CL', skillLevel: 'Advanced', joinedDate: '2023-08-14' },
        { id: 17, name: 'Daniel Yu', avatar: 'DY', skillLevel: 'Advanced', joinedDate: '2023-06-28' }
      ],
      isBooked: false,
      status: 'full',
      equipment: 'Bring your own professional paddle',
      notes: 'Intense training session focusing on tournament strategies and advanced techniques.',
      courtDetails: {
        address: '789 Aberdeen Main Road, Aberdeen, Hong Kong',
        facilities: ['Outdoor Courts', 'Spectator Seating', 'Equipment Storage'],
        surface: 'Outdoor Composite Court',
        lighting: 'Floodlights Available'
      }
    }
  ]);

  const myBookedSessions = sessions.filter(session => 
    session.isBooked || (user && session.creatorId === user.id)
  );
  const availableSessions = sessions.filter(session => 
    !session.isBooked && !(user && session.creatorId === user.id)
  );

  const handleBookSession = (session: any) => {
    if (!user) {
      navigate('/login');
      return;
    }
    setSelectedSession(session);
    setShowBookingModal(true);
    setBookingSuccess(false);
  };

  const handleViewDetails = (session: any) => {
    setSelectedSession(session);
    setShowDetailModal(true);
  };

  const handleCreateSession = () => {
    if (!user) {
      navigate('/login');
      return;
    }
    setShowCreateModal(true);
    // Reset form
    setCreateForm({
      title: '',
      description: '',
      courtId: '',
      date: '',
      startTime: '',
      endTime: '',
      maxParticipants: 4,
      skillLevel: 'Mixed',
      price: '',
      equipment: '',
      notes: ''
    });
  };

  const handleCreateFormChange = (field: keyof CreateSessionForm, value: string | number) => {
    setCreateForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateCreateForm = () => {
    const { title, courtId, date, startTime, endTime } = createForm;
    
    if (!title.trim()) return t('session_title_required', 'Session title is required');
    if (!courtId) return t('please_select_court', 'Please select a court');
    if (!date) return t('please_select_date', 'Please select a date');
    if (!startTime) return t('please_select_start_time', 'Please select start time');
    if (!endTime) return t('please_select_end_time', 'Please select end time');
    
    // Check if date is in the future
    const selectedDate = new Date(date + 'T' + startTime);
    const now = new Date();
    if (selectedDate <= now) return t('session_future_time', 'Session must be scheduled for a future date and time');
    
    // Check if end time is after start time
    if (endTime <= startTime) return t('end_time_after_start', 'End time must be after start time');
    
    return null;
  };

  const submitCreateSession = async () => {
    const validationError = validateCreateForm();
    if (validationError) {
      alert(validationError);
      return;
    }

    if (!user) return;

    setCreateLoading(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));

      const selectedCourt = availableCourts.find(court => court.id === createForm.courtId);
      
      // Generate new session ID
      const newSessionId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      
      // Create new session object
      const newSession = {
        id: newSessionId,
        title: createForm.title,
        date: createForm.date,
        time: `${createForm.startTime} - ${createForm.endTime}`,
        court: selectedCourt?.name || 'Unknown Court',
        courtId: parseInt(createForm.courtId),
        creator: user.user_metadata?.full_name || user.email?.split('@')[0] || 'You',
        creatorId: user.id,
        maxPlayers: createForm.maxParticipants,
        currentPlayers: 1, // Creator is automatically enrolled
        skillLevel: createForm.skillLevel,
        description: createForm.description || 'Join us for a great pickleball session!',
        price: createForm.price ? `HK$${createForm.price} per person` : 'Free',
        participants: [
          {
            id: user.id,
            name: user.user_metadata?.full_name || user.email?.split('@')[0] || 'You',
            avatar: (user.user_metadata?.full_name || user.email?.split('@')[0] || 'You').split(' ').map(n => n[0]).join('').toUpperCase(),
            skillLevel: createForm.skillLevel,
            joinedDate: new Date().toISOString().split('T')[0]
          }
        ],
        isBooked: true, // Creator is automatically booked
        status: 'open',
        equipment: createForm.equipment || 'Please bring your own paddle',
        notes: createForm.notes || 'Looking forward to a great game!',
        courtDetails: {
          address: selectedCourt?.address || selectedCourt?.location || 'Address not available',
          facilities: ['Parking Available', 'Restrooms', 'Water Fountain'],
          surface: 'Outdoor Hard Court',
          lighting: 'Natural Light'
        }
      };

      // Add new session to the list
      setSessions(prevSessions => [newSession, ...prevSessions]);

      // Close modal and show success
      setShowCreateModal(false);
      
      // Automatically switch to "My Bookings" tab to show the created session
      setActiveTab('my-bookings');
      
      // Show success message
      alert(t('auto_enrolled_message', 'Session created successfully! You are automatically enrolled as the first participant.'));

    } catch (error) {
      console.error('Error creating session:', error);
      alert(t('failed_create_session', 'Failed to create session. Please try again.'));
    } finally {
      setCreateLoading(false);
    }
  };

  // Get minimum date (today)
  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  // Get minimum time (current time if date is today)
  const getMinTime = () => {
    if (createForm.date === getMinDate()) {
      const now = new Date();
      return now.toTimeString().slice(0, 5);
    }
    return '';
  };

  // Beautiful Calendar Component
  const CalendarPicker = () => {
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState(createForm.date);
    const { i18n } = useTranslation();

    const today = new Date();
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    // Get first day of month and number of days
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    // Generate calendar days
    const calendarDays = [];
    
    // Previous month's trailing days
    const prevMonth = new Date(year, month - 1, 0);
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      calendarDays.push({
        day: prevMonth.getDate() - i,
        isCurrentMonth: false,
        isToday: false,
        isPast: true,
        date: new Date(year, month - 1, prevMonth.getDate() - i)
      });
    }
    
    // Current month days
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const isToday = date.toDateString() === today.toDateString();
      const isPast = date < today && !isToday;
      
      calendarDays.push({
        day,
        isCurrentMonth: true,
        isToday,
        isPast,
        date
      });
    }
    
    // Next month's leading days
    const remainingDays = 42 - calendarDays.length; // 6 rows × 7 days
    for (let day = 1; day <= remainingDays; day++) {
      calendarDays.push({
        day,
        isCurrentMonth: false,
        isToday: false,
        isPast: false,
        date: new Date(year, month + 1, day)
      });
    }

    // Language-specific month and day names
    const isChineseLanguage = i18n.language === 'zh-TW' || i18n.language === 'zh-CN' || i18n.language === 'zh';
    
    const monthNames = isChineseLanguage ? [
      '一月', '二月', '三月', '四月', '五月', '六月',
      '七月', '八月', '九月', '十月', '十一月', '十二月'
    ] : [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];

    const dayNames = isChineseLanguage ? 
      ['日', '一', '二', '三', '四', '五', '六'] :
      ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    const quickSelectLabels = isChineseLanguage ? {
      today: '今天',
      tomorrow: '明天',
      dayAfter: '後天',
      nextWeek: '下週'
    } : {
      today: 'Today',
      tomorrow: 'Tomorrow',
      dayAfter: 'Day After',
      nextWeek: 'Next Week'
    };

    const goToPrevMonth = () => {
      setCurrentMonth(new Date(year, month - 1));
    };

    const goToNextMonth = () => {
      setCurrentMonth(new Date(year, month + 1));
    };

    const selectDate = (date: Date) => {
      if (date < today && !isToday(date)) return; // Don't allow past dates
      
      const dateString = date.toISOString().split('T')[0];
      setSelectedDate(dateString);
      handleCreateFormChange('date', dateString);
    };

    const isToday = (date: Date) => {
      return date.toDateString() === today.toDateString();
    };

    const isSelected = (date: Date) => {
      return date.toISOString().split('T')[0] === selectedDate;
    };

    return (
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4">
        {/* Calendar Header */}
        <div className="flex items-center justify-between mb-4">
          <button
            type="button"
            onClick={goToPrevMonth}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-gray-600"></i>
          </button>
          
          <h3 className="text-lg font-semibold text-gray-900">
            {monthNames[month]} {year}
          </h3>
          
          <button
            type="button"
            onClick={goToNextMonth}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
          >
            <i className="ri-arrow-right-s-line text-gray-600"></i>
          </button>
        </div>

        {/* Day Names */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {dayNames.map(day => (
            <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-1">
          {calendarDays.map((calendarDay, index) => {
            const { day, isCurrentMonth, isToday: isDayToday, isPast, date } = calendarDay;
            
            return (
              <button
                key={index}
                type="button"
                onClick={() => selectDate(date)}
                disabled={isPast && !isDayToday}
                className={`
                  h-10 w-10 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer
                  ${!isCurrentMonth 
                    ? 'text-gray-300 hover:bg-gray-50' 
                    : isPast && !isDayToday
                      ? 'text-gray-300 cursor-not-allowed'
                      : isDayToday
                        ? 'bg-blue-100 text-blue-600 font-semibold hover:bg-blue-200'
                        : isSelected(date)
                          ? 'bg-teal-600 text-white font-semibold shadow-md'
                          : 'text-gray-700 hover:bg-teal-50 hover:text-teal-600'
                  }
                `}
              >
                {day}
              </button>
            );
          })}
        </div>

        {/* Quick Date Selection */}
        <div className="mt-4 pt-4 border-t border-gray-100">
          <p className="text-xs text-gray-500 mb-2">{isChineseLanguage ? '快速選擇：' : 'Quick select:'}</p>
          <div className="flex gap-2">
            {[0, 1, 2, 6].map(daysFromNow => {
              const quickDate = new Date();
              quickDate.setDate(today.getDate() + daysFromNow);
              const dateString = quickDate.toISOString().split('T')[0];
              const label = daysFromNow === 0 ? quickSelectLabels.today : 
                          daysFromNow === 1 ? quickSelectLabels.tomorrow : 
                          daysFromNow === 2 ? quickSelectLabels.dayAfter : quickSelectLabels.nextWeek;
              
              return (
                <button
                  key={daysFromNow}
                  type="button"
                  onClick={() => {
                    setSelectedDate(dateString);
                    handleCreateFormChange('date', dateString);
                  }}
                  className={`px-3 py-1 text-xs rounded-full transition-colors cursor-pointer ${
                    selectedDate === dateString
                      ? 'bg-teal-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // Beautiful Time Picker Component
  const TimePicker = ({ label, value, onChange, minTime, isEndTime = false }: {
    label: string;
    value: string;
    onChange: (time: string) => void;
    minTime?: string;
    isEndTime?: boolean;
  }) => {
    const [showDropdown, setShowDropdown] = useState(false);
    
    // Generate time slots (every 30 minutes)
    const generateTimeSlots = () => {
      const slots = [];
      for (let hour = 6; hour <= 22; hour++) {
        for (let minute = 0; minute < 60; minute += 30) {
          const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
          
          // Skip if before minimum time
          if (minTime && timeString < minTime) continue;
          
          // For end time, skip if not after start time
          if (isEndTime && createForm.startTime && timeString <= createForm.startTime) continue;
          
          slots.push({
            value: timeString,
            label: timeString,
            period: hour < 12 ? 'AM' : 'PM',
            displayTime: `${hour > 12 ? hour - 12 : hour === 0 ? 12 : hour}:${minute.toString().padStart(2, '0')} ${hour < 12 ? 'AM' : 'PM'}`
          });
        }
      }
      return slots;
    };

    const timeSlots = generateTimeSlots();
    const selectedSlot = timeSlots.find(slot => slot.value === value);

    return (
      <div className="relative">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label} *
        </label>
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowDropdown(!showDropdown)}
            className="w-full border border-gray-300 rounded-lg px-4 py-3 text-left bg-white hover:border-teal-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-colors cursor-pointer flex items-center justify-between"
          >
            <div className="flex items-center">
              <i className="ri-time-line text-teal-600 mr-3"></i>
              <span className={value ? 'text-gray-900' : 'text-gray-500'}>
                {selectedSlot ? selectedSlot.displayTime : `Select ${label.toLowerCase()}`}
              </span>
            </div>
            <i className={`ri-arrow-down-s-line text-gray-400 transition-transform ${showDropdown ? 'rotate-180' : ''}`}></i>
          </button>

          {showDropdown && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-64 overflow-y-auto">
              <div className="p-2">
                <div className="grid grid-cols-2 gap-1">
                  {timeSlots.map((slot) => (
                    <button
                      key={slot.value}
                      type="button"
                      onClick={() => {
                        onChange(slot.value);
                        setShowDropdown(false);
                      }}
                      className={`px-3 py-2 text-sm rounded-md transition-colors cursor-pointer text-left ${
                        value === slot.value
                          ? 'bg-teal-600 text-white'
                          : 'hover:bg-teal-50 text-gray-700'
                      }`}
                    >
                      <div className="font-medium">{slot.displayTime}</div>
                      <div className="text-xs opacity-75">{slot.value}</div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const handleGroupChat = (session: any) => {
    if (!user) {
      navigate('/login');
      return;
    }
    setSelectedSession(session);
    setShowChatModal(true);
    loadChatMessages(session.id);
  };

  const loadChatMessages = async (sessionId: string) => {
    try {
      const { data, error } = await supabase
        .from('session_messages')
        .select(`
          id,
          session_id,
          user_id,
          message,
          created_at,
          profiles (
            nickname,
            avatar_url
          )
        `)
        .eq('session_id', sessionId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setChatMessages(data || []);
    } catch (error) {
      console.error('Error loading chat messages:', error);
      // Fallback to mock data for demo
      setChatMessages([
        {
          id: '1',
          session_id: sessionId,
          user_id: '2',
          message: 'Looking forward to this session! Should we warm up together?',
          created_at: '2024-01-19T10:30:00Z',
          profiles: {
            nickname: 'Sarah Chen',
            avatar_url: null
          }
        },
        {
          id: '2',
          session_id: sessionId,
          user_id: '3',
          message: "Great idea! I'll bring some extra balls just in case.",
          created_at: '2024-01-19T11:15:00Z',
          profiles: {
            nickname: 'Mike Wong',
            avatar_url: null
          }
        },
        {
          id: '3',
          session_id: sessionId,
          user_id: '4',
          message: 'Perfect! See you all there. The weather looks great for outdoor play.',
          created_at: '2024-01-19T14:20:00Z',
          profiles: {
            nickname: 'Lisa Park',
            avatar_url: null
          }
        }
      ]);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedSession || !user || sendingMessage) return;

    setSendingMessage(true);
    
    try {
      const messageData = {
        session_id: selectedSession.id.toString(),
        user_id: user.id,
        message: newMessage.trim()
      };

      const { data, error } = await supabase
        .from('session_messages')
        .insert([messageData])
        .select(`
          id,
          session_id,
          user_id,
          message,
          created_at,
          profiles (
            nickname,
            avatar_url
          )
        `)
        .single();

      if (error) throw error;

      // Add message to local state
      setChatMessages(prev => [...prev, data]);
      setNewMessage('');
      
    } catch (error) {
      console.error('Error sending message:', error);
      // Fallback for demo - add message locally
      const mockMessage: SessionMessage = {
        id: Date.now().toString(),
        session_id: selectedSession.id.toString(),
        user_id: user.id,
        message: newMessage.trim(),
        created_at: new Date().toISOString(),
        profiles: {
          nickname: user.user_metadata?.full_name || user.email?.split('@')[0] || 'You',
          avatar_url: null
        }
      };
      setChatMessages(prev => [...prev, mockMessage]);
      setNewMessage('');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatMessages]);

  const formatMessageTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getMessageSender = (message: SessionMessage) => {
    return message.profiles?.nickname || 'Unknown User';
  };

  const isMyMessage = (message: SessionMessage) => {
    return user && message.user_id === user.id;
  };

  const confirmBooking = async () => {
    if (!selectedSession || !user) return;

    setBookingLoading(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Update session to mark as booked
      setSessions(prevSessions => prevSessions.map(session => {
        if (session.id === selectedSession.id) {
          return {
            ...session,
            isBooked: true,
            currentPlayers: session.currentPlayers + 1,
            participants: [
              ...session.participants,
              {
                id: user.id,
                name: user.user_metadata?.full_name || user.email?.split('@')[0] || 'You',
                avatar: (user.user_metadata?.full_name || user.email?.split('@')[0] || 'You').split(' ').map(n => n[0]).join('').toUpperCase(),
                skillLevel: 'Intermediate',
                joinedDate: new Date().toISOString().split('T')[0]
              }
            ]
          };
        }
        return session;
      }));

      // Show success state
      setBookingSuccess(true);

      // Close modal after delay
      setTimeout(() => {
        setShowBookingModal(false);
        setBookingSuccess(false);
      }, 2000);

    } catch (error) {
      console.error('Error booking session:', error);
      alert('Failed to book session. Please try again.');
    } finally {
      setBookingLoading(false);
    }
  };

  // New tab change handler
  const handleTabChange = (tab: 'upcoming' | 'my-bookings') => {
    setActiveTab(tab);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-teal-600 animate-spin mb-4"></i>
          <p className="text-gray-600">{t('loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-teal-600 to-blue-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl font-bold mb-4">{t('sessions_title', 'Pickleball Sessions')}</h1>
            <p className="text-xl text-teal-100">{t('sessions_subtitle', 'Join or create sessions with fellow players')}</p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Action Bar */}
        <div className="flex flex-col sm:flex-row justify-between items-center mb-8">
          {/* Updated Tab Navigation */}
          <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg mb-6">
            <button
              onClick={() => handleTabChange('upcoming')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                activeTab === 'upcoming'
                  ? 'bg-teal-600 text-white'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
              }`}
            >
              {t('available_sessions', 'Available Sessions')}
            </button>
            <button
              onClick={() => handleTabChange('my-bookings')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                activeTab === 'my-bookings'
                  ? 'bg-teal-600 text-white'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
              }`}
            >
              {t('my_bookings', 'My Bookings')}
            </button>
          </div>
          
          <button 
            onClick={handleCreateSession}
            className="bg-teal-600 text-white px-6 py-3 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-add-line mr-2"></i>
            {t('create_session', 'Create Session')}
          </button>
        </div>

        {/* Sessions List */}
        <div className="space-y-6">
          {(activeTab === 'upcoming' ? availableSessions : myBookedSessions).map(session => (
            <div key={session.id} className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                  <div className="flex-1">
                    <div className="flex items-center mb-3">
                      <h3 className="text-xl font-semibold text-gray-900 mr-3">{session.title}</h3>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getSkillLevelColor(session.skillLevel)}`}>
                        {t(`skill_${session.skillLevel.toLowerCase()}`, session.skillLevel)}
                      </span>
                      {session.status === 'full' && (
                        <span className="ml-2 px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-medium">
                          {t('full', 'Full')}
                        </span>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div className="flex items-center text-gray-600">
                        <i className="ri-calendar-line mr-2 text-teal-600"></i>
                        <span>{session.date}</span>
                      </div>
                      <div className="flex items-center text-gray-600">
                        <i className="ri-time-line mr-2 text-teal-600"></i>
                        <span>{session.time}</span>
                      </div>
                      <div className="flex items-center text-gray-600">
                        <i className="ri-map-pin-line mr-2 text-teal-600"></i>
                        <span>{session.court}</span>
                      </div>
                    </div>
                    
                    <p className="text-gray-700 mb-4">{session.description}</p>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center text-sm text-gray-600">
                          <i className="ri-user-line mr-1 text-teal-600"></i>
                          <span>{session.currentPlayers}/{session.maxPlayers} {t('players', 'players')}</span>
                        </div>
                        <div className="flex items-center text-sm text-gray-600">
                          <i className="ri-price-tag-3-line mr-1 text-teal-600"></i>
                          <span>{session.price}</span>
                        </div>
                        <div className="flex items-center text-sm text-gray-600">
                          <i className="ri-user-star-line mr-1 text-teal-600"></i>
                          <span>{t('organized_by', 'Organized by')} {session.creator}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-4 lg:mt-0 lg:ml-6 flex flex-col space-y-2">
                    {session.isBooked ? (
                      <>
                        <button className="bg-green-100 text-green-800 px-4 py-2 rounded-lg font-medium whitespace-nowrap">
                          <i className="ri-check-line mr-2"></i>
                          {t('booked', 'Booked')}
                        </button>
                        <button 
                          onClick={() => handleGroupChat(session)}
                          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
                        >
                          <i className="ri-chat-3-line mr-2"></i>
                          {t('group_chat', 'Group Chat')}
                        </button>
                      </>
                    ) : (
                      <>
                        {session.status === 'full' ? (
                          <button disabled className="bg-gray-300 text-gray-500 px-4 py-2 rounded-lg cursor-not-allowed whitespace-nowrap">
                            {t('session_full', 'Session Full')}
                          </button>
                        ) : (
                          <button
                            onClick={() => handleBookSession(session)}
                            className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
                          >
                            {t('book_session', 'Book Session')}
                          </button>
                        )}
                        <button 
                          onClick={() => handleViewDetails(session)}
                          className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                        >
                          {t('view_details', 'View Details')}
                        </button>
                      </>
                    )}
                  </div>
                </div>
                
                {/* Participants */}
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">{t('participants', 'Participants')}</h4>
                  <div className="flex flex-wrap gap-2">
                    {session.participants.map(participant => (
                      <div key={participant.id} className="flex items-center bg-gray-100 rounded-full px-3 py-1">
                        <div className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center mr-2">
                          <span className="text-xs font-medium text-teal-600">{participant.avatar}</span>
                        </div>
                        <span className="text-sm text-gray-700">{participant.name}</span>
                      </div>
                    ))}
                    {session.currentPlayers < session.maxPlayers && (
                      <div className="flex items-center bg-gray-50 border-2 border-dashed border-gray-300 rounded-full px-3 py-1">
                        <i className="ri-add-line text-gray-400 mr-1"></i>
                        <span className="text-sm text-gray-500">
                          {session.maxPlayers - session.currentPlayers} {t('spots_left', 'spots left')}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {(activeTab === 'upcoming' ? availableSessions : myBookedSessions).length === 0 && (
          <div className="text-center py-12">
            <i className="ri-calendar-line text-6xl text-gray-300 mb-4"></i>
            <h3 className="text-xl font-medium text-gray-900 mb-2">
              {activeTab === 'upcoming' ? t('no_sessions_available', 'No sessions available') : t('no_booked_sessions', 'No booked sessions')}
            </h3>
            <p className="text-gray-600 mb-6">
              {activeTab === 'upcoming' 
                ? t('no_sessions_message', 'Check back later for new sessions or create your own!')
                : t('no_booked_message', 'Book a session to start playing with the community!')
              }
            </p>
            <button 
              onClick={activeTab === 'upcoming' ? handleCreateSession : () => setActiveTab('upcoming')}
              className="bg-teal-600 text-white px-6 py-3 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              {activeTab === 'upcoming' ? t('create_session', 'Create Session') : t('browse_sessions', 'Browse Sessions')}
            </button>
          </div>
        )}
      </div>

      {/* Create Session Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-gray-900">{t('create_new_session', 'Create New Session')}</h3>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Left Column - Form */}
                <div className="space-y-6">
                  {/* Session Basic Info */}
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-900">{t('session_information', 'Session Information')}</h4>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('session_title', 'Session Title')} *
                      </label>
                      <input
                        type="text"
                        value={createForm.title}
                        onChange={(e) => handleCreateFormChange('title', e.target.value)}
                        placeholder={t('session_title_placeholder', 'e.g., Morning Doubles Practice')}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                        maxLength={100}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('description', 'Description')}
                      </label>
                      <textarea
                        value={createForm.description}
                        onChange={(e) => handleCreateFormChange('description', e.target.value)}
                        placeholder={t('description_placeholder', 'Describe your session, what to expect, skill level requirements...')}
                        rows={3}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                        maxLength={500}
                      />
                      <p className="text-xs text-gray-500 mt-1">{createForm.description.length}/500 {t('characters', 'characters')}</p>
                    </div>
                  </div>

                  {/* Court Selection */}
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-900">{t('court_schedule', 'Court & Schedule')}</h4>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('select_court', 'Select Court')} *
                      </label>
                      {courtsLoading ? (
                        <div className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-500">
                          <i className="ri-loader-4-line animate-spin mr-2"></i>
                          Loading courts...
                        </div>
                      ) : (
                        <select
                          value={createForm.courtId}
                          onChange={(e) => handleCreateFormChange('courtId', e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent pr-8"
                        >
                          <option value="">{t('choose_court', 'Choose a court...')}</option>
                          {availableCourts.map(court => (
                            <option key={court.id} value={court.id}>
                              {court.name} - {court.location}, {court.district}
                            </option>
                          ))}
                        </select>
                      )}
                      <p className="text-xs text-gray-500 mt-1">
                        {availableCourts.length} courts available across Hong Kong
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <TimePicker
                        label={t('start_time', 'Start Time')}
                        value={createForm.startTime}
                        onChange={(time) => handleCreateFormChange('startTime', time)}
                        minTime={getMinTime()}
                      />
                      
                      <TimePicker
                        label={t('end_time', 'End Time')}
                        value={createForm.endTime}
                        onChange={(time) => handleCreateFormChange('endTime', time)}
                        minTime={createForm.startTime}
                        isEndTime={true}
                      />
                    </div>
                  </div>

                  {/* Session Settings */}
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-900">{t('session_settings', 'Session Settings')}</h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('maximum_participants', 'Maximum Participants')}
                        </label>
                        <select
                          value={createForm.maxParticipants}
                          onChange={(e) => handleCreateFormChange('maxParticipants', parseInt(e.target.value))}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent pr-8"
                        >
                          <option value={2}>2{t('players_singles', ' Players (Singles)')}</option>
                          <option value={4}>4{t('players_doubles', ' Players (Doubles)')}</option>
                          <option value={6}>6{t('players_round_robin', ' Players (Round Robin)')}</option>
                          <option value={8}>8{t('players_tournament', ' Players (Tournament)')}</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('skill_level', 'Skill Level')}
                        </label>
                        <select
                          value={createForm.skillLevel}
                          onChange={(e) => handleCreateFormChange('skillLevel', e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent pr-8"
                        >
                          <option value="Mixed">{t('skill_mixed', 'Mixed (All Levels)')}</option>
                          <option value="Beginner">{t('skill_beginner', 'Beginner')}</option>
                          <option value="Intermediate">{t('skill_intermediate', 'Intermediate')}</option>
                          <option value="Advanced">{t('skill_advanced', 'Advanced')}</option>
                          <option value="Expert">{t('skill_expert', 'Expert')}</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('price_per_person', 'Price per Person')} (HK$)
                      </label>
                      <input
                        type="number"
                        value={createForm.price}
                        onChange={(e) => handleCreateFormChange('price', e.target.value)}
                        placeholder={t('price_placeholder', 'Leave empty for free session')}
                        min="0"
                        max="1000"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      />
                      <p className="text-xs text-gray-500 mt-1">{t('price_note', 'Leave empty to create a free session')}</p>
                    </div>
                  </div>

                  {/* Additional Details */}
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-900">{t('additional_details', 'Additional Details')}</h4>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('equipment_requirements', 'Equipment & Requirements')}
                      </label>
                      <input
                        type="text"
                        value={createForm.equipment}
                        onChange={(e) => handleCreateFormChange('equipment', e.target.value)}
                        placeholder={t('equipment_placeholder', 'e.g., Paddles provided, bring water bottle')}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                        maxLength={200}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('additional_notes', 'Additional Notes')}
                      </label>
                      <textarea
                        value={createForm.notes}
                        onChange={(e) => handleCreateFormChange('notes', e.target.value)}
                        placeholder={t('notes_placeholder', 'Any additional information for participants...')}
                        rows={3}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                        maxLength={300}
                      />
                      <p className="text-xs text-gray-500 mt-1">{createForm.notes.length}/300 {t('characters', 'characters')}</p>
                    </div>
                  </div>
                </div>

                {/* Right Column - Calendar */}
                <div className="space-y-6">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-4">{t('select_date', 'Select Date')} *</h4>
                    <CalendarPicker />
                  </div>

                  {/* Session Preview */}
                  {createForm.title && createForm.date && createForm.startTime && createForm.endTime && (
                    <div className="bg-gradient-to-br from-teal-50 to-blue-50 rounded-xl p-4 border border-teal-200">
                      <h5 className="font-semibold text-gray-900 mb-3 flex items-center">
                        <i className="ri-eye-line mr-2 text-teal-600"></i>
                        Session Preview
                      </h5>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center text-gray-700">
                          <i className="ri-calendar-line mr-2 text-teal-600"></i>
                          <span>{createForm.date}</span>
                        </div>
                        <div className="flex items-center text-gray-700">
                          <i className="ri-time-line mr-2 text-teal-600"></i>
                          <span>{createForm.startTime} - {createForm.endTime}</span>
                        </div>
                        {createForm.courtId && (
                          <div className="flex items-center text-gray-700">
                            <i className="ri-map-pin-line mr-2 text-teal-600"></i>
                            <span>{availableCourts.find(c => c.id === createForm.courtId)?.name}</span>
                          </div>
                        )}
                        <div className="flex items-center text-gray-700">
                          <i className="ri-user-line mr-2 text-teal-600"></i>
                          <span>Max {createForm.maxParticipants} players</span>
                        </div>
                        <div className="flex items-center text-gray-700">
                          <i className="ri-price-tag-3-line mr-2 text-teal-600"></i>
                          <span>{createForm.price ? `HK$${createForm.price} per person` : 'Free'}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-3 pt-6 mt-6 border-t border-gray-200">
                <button
                  onClick={() => setShowCreateModal(false)}
                  disabled={createLoading}
                  className="flex-1 border border-gray-300 text-gray-700 py-3 px-4 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
                >
                  {t('cancel', 'Cancel')}
                </button>
                <button
                  onClick={submitCreateSession}
                  disabled={createLoading}
                  className="flex-1 bg-teal-600 text-white py-3 px-4 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 flex items-center justify-center"
                >
                  {createLoading ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      {t('creating_session', 'Creating Session...')}
                    </>
                  ) : (
                    <>
                      <i className="ri-add-line mr-2"></i>
                      {t('create_session', 'Create Session')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Group Chat Modal */}
      {showChatModal && selectedSession && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full h-[600px] flex flex-col">
            {/* Chat Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{selectedSession.title}</h3>
                <p className="text-sm text-gray-600">{selectedSession.currentPlayers} {t('participants', 'participants')}</p>
              </div>
              <button
                onClick={() => setShowChatModal(false)}
                className="text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            {/* Chat Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {chatMessages.length === 0 ? (
                <div className="text-center py-8">
                  <i className="ri-chat-3-line text-4xl text-gray-300 mb-4"></i>
                  <h4 className="text-lg font-medium text-gray-900 mb-2">{t('no_messages_yet', 'No messages yet')}</h4>
                  <p className="text-gray-600">{t('start_conversation', 'Start the conversation with your fellow players!')}</p>
                </div>
              ) : (
                chatMessages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${isMyMessage(message) ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-xs lg:max-w-md ${isMyMessage(message) ? 'order-2' : 'order-1'}`}>
                      <div
                        className={`px-4 py-2 rounded-lg ${
                          isMyMessage(message)
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-900'
                        }`}
                      >
                        {!isMyMessage(message) && (
                          <p className="text-xs font-medium mb-1 opacity-75">
                            {getMessageSender(message)}
                          </p>
                        )}
                        <p className="text-sm">{message.message}</p>
                        <p className={`text-xs mt-1 ${isMyMessage(message) ? 'text-blue-100' : 'text-gray-500'}`}>
                          {formatMessageTime(message.created_at)}
                        </p>
                      </div>
                    </div>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${
                      isMyMessage(message) ? 'order-1 mr-2 bg-blue-100 text-blue-600' : 'order-2 ml-2 bg-gray-200 text-gray-600'
                    }`}>
                      {getMessageSender(message).split(' ').map(n => n[0]).join('').toUpperCase()}
                    </div>
                  </div>
                ))
              )}
              <div ref={chatEndRef} />
            </div>

            {/* Message Input */}
            <div className="p-4 border-t border-gray-200">
              <div className="flex space-x-2">
                <textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={t('type_your_message', 'Type your message...')}
                  maxLength={500}
                  rows={2}
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button
                  onClick={sendMessage}
                  disabled={!newMessage.trim() || sendingMessage}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {sendingMessage ? (
                    <i className="ri-loader-4-line animate-spin"></i>
                  ) : (
                    <i className="ri-send-plane-line"></i>
                  )}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">{newMessage.length}/500 {t('characters', 'characters')}</p>
            </div>
          </div>
        </div>
      )}

      {/* Session Detail Modal */}
      {showDetailModal && selectedSession && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-gray-900">{t('session_details', 'Session Details')}</h3>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Session Header */}
              <div>
                <div className="flex items-center mb-3">
                  <h4 className="text-2xl font-bold text-gray-900 mr-3">{selectedSession.title}</h4>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getSkillLevelColor(selectedSession.skillLevel)}`}>
                    {t(`skill_${selectedSession.skillLevel.toLowerCase()}`, selectedSession.skillLevel)}
                  </span>
                  {selectedSession.status === 'full' && (
                    <span className="ml-2 px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-medium">
                      {t('full', 'Full')}
                    </span>
                  )}
                </div>
                <p className="text-gray-700 text-lg">{selectedSession.description}</p>
              </div>

              {/* Session Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h5 className="font-semibold text-gray-900">{t('session_information', 'Session Information')}</h5>
                  <div className="space-y-3">
                    <div className="flex items-center text-gray-600">
                      <i className="ri-calendar-line mr-3 text-teal-600"></i>
                      <span>{selectedSession.date}</span>
                    </div>
                    <div className="flex items-center text-gray-600">
                      <i className="ri-time-line mr-3 text-teal-600"></i>
                      <span>{selectedSession.time}</span>
                    </div>
                    <div className="flex items-center text-gray-600">
                      <i className="ri-user-line mr-3 text-teal-600"></i>
                      <span>{selectedSession.currentPlayers}/{selectedSession.maxPlayers} {t('players', 'players')}</span>
                    </div>
                    <div className="flex items-center text-gray-600">
                      <i className="ri-price-tag-3-line mr-3 text-teal-600"></i>
                      <span>{selectedSession.price}</span>
                    </div>
                    <div className="flex items-center text-gray-600">
                      <i className="ri-user-star-line mr-3 text-teal-600"></i>
                      <span>{t('organized_by', 'Organized by')} {selectedSession.creator}</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h5 className="font-semibold text-gray-900">{t('court_details', 'Court Details')}</h5>
                  <div className="space-y-3">
                    <div className="flex items-start text-gray-600">
                      <i className="ri-map-pin-line mr-3 text-teal-600 mt-1"></i>
                      <div>
                        <div className="font-medium">{selectedSession.court}</div>
                        <div className="text-sm">{selectedSession.courtDetails.address}</div>
                      </div>
                    </div>
                    <div className="flex items-center text-gray-600">
                      <i className="ri-road-map-line mr-3 text-teal-600"></i>
                      <span>{selectedSession.courtDetails.surface}</span>
                    </div>
                    <div className="flex items-center text-gray-600">
                      <i className="ri-lightbulb-line mr-3 text-teal-600"></i>
                      <span>{selectedSession.courtDetails.lighting}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Facilities */}
              <div>
                <h5 className="font-semibold text-gray-900 mb-3">{t('court_facilities', 'Court Facilities')}</h5>
                <div className="flex flex-wrap gap-2">
                  {selectedSession.courtDetails.facilities.map((facility, index) => (
                    <span key={index} className="bg-teal-50 text-teal-700 px-3 py-1 rounded-full text-sm">
                      {facility}
                    </span>
                  ))}
                </div>
              </div>

              {/* Equipment & Notes */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h5 className="font-semibold text-gray-900 mb-3">{t('equipment', 'Equipment')}</h5>
                  <p className="text-gray-600">{selectedSession.equipment}</p>
                </div>
                <div>
                  <h5 className="font-semibold text-gray-900 mb-3">{t('additional_notes', 'Additional Notes')}</h5>
                  <p className="text-gray-600">{selectedSession.notes}</p>
                </div>
              </div>

              {/* Participants */}
              <div>
                <h5 className="font-semibold text-gray-900 mb-4">{t('participants', 'Participants')} ({selectedSession.currentPlayers}/{selectedSession.maxPlayers})</h5>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {selectedSession.participants.map(participant => (
                    <div key={participant.id} className="flex items-center bg-gray-50 rounded-lg p-3">
                      <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center mr-3">
                        <span className="text-sm font-medium text-teal-600">{participant.avatar}</span>
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">{participant.name}</div>
                        <div className="text-sm text-gray-600">{participant.skillLevel} • {t('joined', 'Joined')} {participant.joinedDate}</div>
                      </div>
                    </div>
                  ))}
                  {selectedSession.currentPlayers < selectedSession.maxPlayers && (
                    <div className="flex items-center bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-3">
                      <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center mr-3">
                        <i className="ri-add-line text-gray-400"></i>
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-gray-600">{t('open_spot', 'Open Spot')}</div>
                        <div className="text-sm text-gray-500">{selectedSession.maxPlayers - selectedSession.currentPlayers} {t('spots_remaining', 'spots remaining')}</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-3 pt-4 border-t border-gray-200">
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="flex-1 border border-gray-300 text-gray-700 py-3 px-4 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  {t('close', 'Close')}
                </button>
                {!selectedSession.isBooked && selectedSession.status !== 'full' && (
                  <button
                    onClick={() => {
                      setShowDetailModal(false);
                      handleBookSession(selectedSession);
                    }}
                    className="flex-1 bg-teal-600 text-white py-3 px-4 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-calendar-check-line mr-2"></i>
                    {t('book_this_session', 'Book This Session')}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Booking Modal */}
      {showBookingModal && selectedSession && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">{t('confirm_booking', 'Confirm Booking')}</h3>
              <button
                onClick={() => setShowBookingModal(false)}
                className="text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            
            {bookingSuccess ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-line text-2xl text-green-600"></i>
                </div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">{t('booking_confirmed', 'Booking Confirmed!')}</h4>
                <p className="text-gray-600">{t('booking_success_message', 'You have successfully joined this session.')}</p>
              </div>
            ) : (
              <>
                <div className="space-y-4 mb-6">
                  <div>
                    <h4 className="font-medium text-gray-900">{selectedSession.title}</h4>
                    <p className="text-gray-600">{selectedSession.date} {t('at', 'at')} {selectedSession.time}</p>
                  </div>
                  <div>
                    <p className="text-gray-700">{selectedSession.court}</p>
                    <p className="text-gray-600">{selectedSession.price}</p>
                  </div>
                </div>
                
                <div className="flex space-x-3">
                  <button
                    onClick={() => setShowBookingModal(false)}
                    disabled={bookingLoading}
                    className="flex-1 border border-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
                  >
                    {t('cancel', 'Cancel')}
                  </button>
                  <button
                    onClick={confirmBooking}
                    disabled={bookingLoading}
                    className="flex-1 bg-teal-600 text-white py-2 px-4 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 flex items-center justify-center"
                  >
                    {bookingLoading ? (
                      <>
                        <i className="ri-loader-4-line animate-spin mr-2"></i>
                        {t('booking', 'Booking...')}
                      </>
                    ) : (
                      t('confirm_booking', 'Confirm Booking')
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Sessions;
